import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { useTranslation } from 'react-i18next';
import 'katex/dist/katex.min.css';
import { preprocessMathTags, preprocessBadMathFormats } from '../utils/mathUtils';
import { parseGraphBlocks, replaceGraphsWithPlaceholders, type ParsedGraph } from '../utils/graphUtils';
import { parseTableBlocks, replaceTablesWithPlaceholders, type ParsedTable } from '../utils/tableUtils';
import MathRenderer from './MathRenderer';
import GraphRenderer from './GraphRenderer';
import TableRenderer from './TableRenderer';
import { Minus, Plus } from 'lucide-react';

// Helper function to detect and convert table-like content in code blocks
const detectAndConvertTable = (codeContent: string): string | null => {
  const lines = codeContent.trim().split('\n');
  if (lines.length < 2) return null;
  
  // Look for lines with consistent separators (spaces, dashes, pipes)
  const hasDashedSeparator = lines.some(line => /^-+(\s+-+)*\s*$/.test(line.trim()));
  const hasConsistentColumns = lines.every(line => {
    const parts = line.trim().split(/\s{2,}|\t+/); // Split on 2+ spaces or tabs
    return parts.length >= 2;
  });
  
  if (!hasDashedSeparator && !hasConsistentColumns) return null;
  
  // Try to convert to markdown table
  const tableLines: string[] = [];
  let headerProcessed = false;
  
  for (const line of lines) {
    const trimmedLine = line.trim();
    
    // Skip separator lines with dashes
    if (/^-+(\s+-+)*\s*$/.test(trimmedLine)) {
      if (!headerProcessed) {
        // Add markdown header separator if we haven't added one yet
        const parts = lines[0].trim().split(/\s{2,}|\t+/);
        const separator = '|' + parts.map(() => '---').join('|') + '|';
        tableLines.push(separator);
        headerProcessed = true;
      }
      continue;
    }
    
    // Convert space/tab separated values to pipe-separated
    const parts = trimmedLine.split(/\s{2,}|\t+/);
    if (parts.length >= 2) {
      const markdownRow = '| ' + parts.join(' | ') + ' |';
      tableLines.push(markdownRow);
      
      // Add separator after first row if no dashed separator was found
      if (!headerProcessed && !hasDashedSeparator) {
        const separator = '|' + parts.map(() => '---').join('|') + '|';
        tableLines.push(separator);
        headerProcessed = true;
      }
    }
  }
  
  return tableLines.length >= 2 ? tableLines.join('\n') : null;
};

// Helper function to check if content contains math
const containsMathContent = (children: any): boolean => {
  if (typeof children === 'string') {
    return /\$.*?\$|\\\(.*?\\\)|\\\[.*?\\\]/.test(children);
  }
  if (Array.isArray(children)) {
    return children.some(child => containsMathContent(child));
  }
  if (children && children.props) {
    // Check for KaTeX rendered elements
    if (children.props.className && typeof children.props.className === 'string') {
      if (children.props.className.includes('katex')) {
        return true;
      }
    }
    return containsMathContent(children.props.children);
  }
  if (children && typeof children === 'object' && children.type) {
    // Check if it's a React element with katex className
    if (children.props && children.props.className && 
        typeof children.props.className === 'string' &&
        children.props.className.includes('katex')) {
      return true;
    }
  }
  return false;
};

// Preprocess content to handle question formatting
const preprocessQuestionContent = (content: string, materialType: string): string => {
  // Only apply special processing for question-type content
  if (!['question', 'answer_option', 'explanation', 'solution'].includes(materialType)) {
    return content;
  }

  // Split content by line breaks
  const lines = content.split('\n').map(line => line.trim()).filter(line => line.length > 0);
  
  if (lines.length === 0) return content;

  const processedLines: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const currentLine = lines[i];
    
    // Check if this is a bold header (starts with ** or ends with **)
    const isBoldHeader = currentLine.startsWith('**') && currentLine.endsWith('**');
    
    // Check if this line is followed by what looks like multiple choice options
    // Hebrew letters (א-ת) or English letters (a-z, A-Z) followed by period or parenthesis
    const isFollowedByOptions = i < lines.length - 1 && 
      /^[א-ת][\.\))]|^[a-zA-Z][\.\))]/.test(lines[i + 1]) &&
      !lines[i + 1].startsWith('**'); // Don't treat bold headers as options
    
    // Check if current line is an option (but not a bold header)
    const isOption = !isBoldHeader && /^[א-ת][\.\))]|^[a-zA-Z][\.\))]/.test(currentLine);
    
    if (isBoldHeader) {
      // This is a bold header - always add a line break after it
      processedLines.push(currentLine);
      if (i < lines.length - 1) {
        processedLines.push(''); // Add empty line for paragraph break after bold headers
      }
    } else if (isFollowedByOptions && !isOption) {
      // This is likely a lead-in to options (like "מכאן")
      processedLines.push(currentLine);
      processedLines.push(''); // Add empty line for paragraph break
    } else if (isOption) {
      // This is an option - add it as a separate line
      processedLines.push(currentLine);
      // Add line break after each option except the last one
      if (i < lines.length - 1 && /^[א-ת][\.\))]|^[a-zA-Z][\.\))]/.test(lines[i + 1])) {
        processedLines.push('  '); // Two spaces for line break
      }
    } else {
      // Regular content
      processedLines.push(currentLine);
      // Add paragraph break if the next line exists and isn't an option
      if (i < lines.length - 1 && !isOption && !/^[א-ת][\.\))]|^[a-zA-Z][\.\))]/.test(lines[i + 1])) {
        const nextLine = lines[i + 1];
        // Check if next line seems like a new paragraph (not a continuation)
        if (nextLine.length > 0 && 
            !currentLine.endsWith(',') && 
            !currentLine.endsWith('،') && // Arabic comma
            !currentLine.endsWith('-') &&
            !currentLine.endsWith('–') &&
            !currentLine.endsWith('—')) {
          processedLines.push(''); // Add empty line for paragraph break
        }
      }
    }
    
    i++;
  }

  return processedLines.join('\n');
};

interface StudyMaterialRendererProps {
  content: string;
  materialType: string;
  isRTL?: boolean;
  fontSize?: number;
  onFontSizeChange?: (size: number) => void;
  showDevMarkdown?: boolean;
}

const StudyMaterialRenderer: React.FC<StudyMaterialRendererProps> = ({ 
  content, 
  materialType,
  isRTL = true,
  fontSize,
  onFontSizeChange,
  showDevMarkdown = true
}) => {
  const { i18n, t } = useTranslation();
  const effectiveRTL = isRTL ?? i18n.language === 'he';
  
  // Development-only state for raw markdown view
  const [showRawMarkdown, setShowRawMarkdown] = useState(false);
  const [imageModal, setImageModal] = useState<{ src: string; alt?: string } | null>(null);
  
  // Check if this is a question/answer context that needs minimal styling
  const isQuestionContext = ['question', 'answer_option', 'explanation', 'solution'].includes(materialType);

  useEffect(() => {
    if (!imageModal) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setImageModal(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [imageModal]);

  const handleFontSizeChange = (direction: 'increase' | 'decrease') => {
    if (onFontSizeChange && fontSize) {
      const newSize = direction === 'increase' ? fontSize + 1 : fontSize - 1;
      // Add constraints to font size
      if (newSize >= 12 && newSize <= 24) {
        onFontSizeChange(newSize);
      }
    }
  };
  
  // Parse graphs and tables, then replace with placeholders
  const { graphs, tables, processedContent } = useMemo(() => {
    // First preprocess math tags for backward compatibility
    const mathProcessed = preprocessMathTags(content);
    
    // Then, fix incorrect math delimiters produced by the model
    const badFormatProcessed = preprocessBadMathFormats(mathProcessed);

    // Preprocess question content for formatting
    const questionProcessed = preprocessQuestionContent(badFormatProcessed, materialType);

    // If the backend failed to replace a graph:request block, show a readable note instead of raw config
    const cleanedGraphRequests = questionProcessed.replace(/```graph:request[\s\S]*?```/g, (block) => {
      const titleMatch = block.match(/Title:\s*(.*)/);
      const title = titleMatch?.[1]?.trim() || 'Graph';
      return `> Graph rendering failed for "${title}".`;
    });
    
    // Parse graph blocks from content
    const parsedGraphs = parseGraphBlocks(cleanedGraphRequests);
    
    // Parse table blocks from content
    const parsedTables = parseTableBlocks(cleanedGraphRequests);
    
    // Replace graph blocks with placeholders
    let processedWithGraphs = replaceGraphsWithPlaceholders(cleanedGraphRequests, parsedGraphs);
    
    // Replace table blocks with placeholders
    const finalProcessed = replaceTablesWithPlaceholders(processedWithGraphs, parsedTables);
    
    return {
      graphs: parsedGraphs,
      tables: parsedTables,
      processedContent: finalProcessed
    };
  }, [content, materialType]);

  // Custom component overrides for RTL support and styling
  const components = useMemo(() => ({
    p: ({ children }: any) => {
      // Check if this paragraph contains a graph or table placeholder
      const text = typeof children === 'string' ? children : 
                   Array.isArray(children) ? children.join('') : '';
      
      const graphMatch = graphs.find(graph => text.includes(graph.placeholder));
      
      if (graphMatch) {
        return (
          <div className={`${isQuestionContext ? 'my-3' : 'my-6'}`}>
            <GraphRenderer 
              config={graphMatch.config}
              onError={(error) => console.error('Graph render error:', error)}
              isRTL={effectiveRTL}
            />
          </div>
        );
      }
      
      // Check if this paragraph contains ONLY a table placeholder
      if (/^TABLE_\d+$/.test(text.trim())) {
        const tableMatch = tables.find(table => table.placeholder === text.trim());
        
        if (tableMatch) {
          return (
            <div className={`${isQuestionContext ? 'my-3' : 'my-6'}`}>
              <TableRenderer 
                config={tableMatch.config}
                onError={(error) => console.error('Table render error:', error)}
                isRTL={effectiveRTL}
              />
            </div>
          );
        }
      }
      
      // NEW: Handle paragraphs with mixed content that includes table placeholders
      if (Array.isArray(children)) {
        const processedChildren = children.map((child, index) => {
          if (typeof child === 'string') {
            // Check if this string contains table placeholders
            const tableMatches = child.match(/TABLE_\d+/g);
            if (tableMatches) {
              // Split the string by table placeholders and process each part
              const parts = child.split(/(TABLE_\d+)/);
              return parts.map((part, partIndex) => {
                if (/^TABLE_\d+$/.test(part)) {
                  const tableMatch = tables.find(table => table.placeholder === part);
                  if (tableMatch) {
                    return (
                      <div key={`${index}-${partIndex}`} className={`${isQuestionContext ? 'my-3' : 'my-6'}`}>
                        <TableRenderer 
                          config={tableMatch.config}
                          onError={(error) => console.error('Table render error:', error)}
                          isRTL={effectiveRTL}
                        />
                      </div>
                    );
                  }
                  return part; // Return placeholder if no match found
                }
                return part;
              });
            }
          }
          return child;
        }).flat();
        
        return (
          <div>
            {processedChildren}
          </div>
        );
      }
      
      // Check if this is a multiple choice option (Hebrew or English letters followed by . or ) )
      const isMultipleChoiceOption = typeof text === 'string' && 
        /^[א-ת][\.\))]|^[a-zA-Z][\.\))]/.test(text.trim());
      
      // Apply different styling for multiple choice options
      if (isMultipleChoiceOption) {
        return (
          <p className={`${isQuestionContext ? 'mb-2 md:mb-3' : 'mb-3 md:mb-4'} leading-relaxed text-secondary-800 ${effectiveRTL ? 'text-right mr-3 md:mr-4' : 'text-left ml-3 md:ml-4'}`} 
             dir={effectiveRTL ? "rtl" : "ltr"}
             style={{ 
               textIndent: effectiveRTL ? '-1.2em' : '1.2em', 
               paddingRight: effectiveRTL ? '1.2em' : '0',
               paddingLeft: effectiveRTL ? '0' : '1.2em'
             }}>
            {children}
          </p>
        );
      }
      
      return (
        <p className={`${isQuestionContext ? 'mb-2 md:mb-3' : 'mb-3 md:mb-4'} leading-relaxed text-secondary-800 ${effectiveRTL ? 'text-right mr-3 md:mr-4' : 'text-left ml-3 md:ml-4'}`} 
           dir={effectiveRTL ? "rtl" : "ltr"}
           style={{ 
             textIndent: effectiveRTL ? '-1.2em' : '1.2em', 
             paddingRight: effectiveRTL ? '1.2em' : '0',
             paddingLeft: effectiveRTL ? '0' : '1.2em'
           }}>
          {children}
        </p>
      );
    },
    
    // Handle text nodes that might contain table placeholders
    text: ({ children }: any) => {
      const text = String(children);
      
      // Check if this text node is exactly a table placeholder
      if (/^TABLE_\d+$/.test(text.trim())) {
        const tableMatch = tables.find(table => table.placeholder === text.trim());
        
        if (tableMatch) {
          try {
            const tableElement = (
              <div className={`${isQuestionContext ? 'my-3' : 'my-6'}`}>
                <TableRenderer 
                  config={tableMatch.config}
                  onError={(error) => console.error('Table render error:', error)}
                  isRTL={effectiveRTL}
                />
              </div>
            );
            return tableElement;
          } catch (error) {
            console.error('Table render error:', error);
            return <div className="my-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700">Error rendering table</p>
            </div>;
          }
        }
      }
      
      return children;
    },
    ul: ({ children }: any) => (
      <ul className={`list-disc list-inside ${isQuestionContext ? 'mb-2' : 'mb-3 md:mb-4'} text-right`} dir={effectiveRTL ? "rtl" : "ltr"}>
        {children}
      </ul>
    ),
    ol: ({ children }: any) => (
      <ol className={`list-decimal list-inside ${isQuestionContext ? 'mb-2' : 'mb-3 md:mb-4'} text-right`} dir={effectiveRTL ? "rtl" : "ltr"}>
        {children}
      </ol>
    ),
    li: ({ children }: any) => {
      // Check if this list item contains a graph placeholder
      const checkForGraphPlaceholder = (node: any): string => {
        if (typeof node === 'string') return node;
        if (Array.isArray(node)) return node.map(checkForGraphPlaceholder).join('');
        if (node && node.props && node.props.children) {
          return checkForGraphPlaceholder(node.props.children);
        }
        return '';
      };
      
      const fullText = checkForGraphPlaceholder(children);
      const graphMatch = graphs.find(graph => fullText.includes(graph.placeholder));
      
      if (graphMatch) {
        return (
          <li className={`${isQuestionContext ? 'mb-1' : 'mb-2'} text-right text-secondary-700`}>
            <div className={`${isQuestionContext ? 'my-3' : 'my-6'}`}>
              <GraphRenderer 
                config={graphMatch.config}
                onError={(error) => console.error('Graph render error:', error)}
                isRTL={effectiveRTL}
              />
            </div>
          </li>
        );
      }
      
      const tableMatch = tables.find(table => fullText.includes(table.placeholder));
      
      if (tableMatch) {
        return (
          <li className={`${isQuestionContext ? 'mb-1' : 'mb-2'} text-right text-secondary-700`}>
            <div className={`${isQuestionContext ? 'my-3' : 'my-6'}`}>
              <TableRenderer 
                config={tableMatch.config}
                onError={(error) => console.error('Table render error:', error)}
                isRTL={effectiveRTL}
              />
            </div>
          </li>
        );
      }
      
      const hasMath = containsMathContent(children);
      
      if (hasMath) {
        // For list items with math, use special handling
        return (
          <li 
            className="mb-2 text-secondary-700" 
            style={{ 
              direction: effectiveRTL ? 'rtl' : 'ltr',
              textAlign: effectiveRTL ? 'right' : 'left',
              // Ensure inline elements flow correctly with math
              display: 'list-item'
            }}
          >
            <div 
              style={{
                direction: effectiveRTL ? 'rtl' : 'ltr',
                textAlign: effectiveRTL ? 'right' : 'left',
                // Allow proper inline flow for mixed content
                display: 'inline'
              }}
            >
              {children}
            </div>
          </li>
        );
      }
      
      // Regular list items without math
      return (
        <li className={`${isQuestionContext ? 'mb-1' : 'mb-2'} text-right text-secondary-700`}>{children}</li>
      );
    },
    h1: ({ children }: any) => (
      <h1 
        className={`${isQuestionContext ? 'font-bold mb-2 mt-2' : 'font-bold mb-3 md:mb-4 mt-4 md:mt-6'} first:mt-0 text-right text-primary-800`} 
        dir={effectiveRTL ? "rtl" : "ltr"}
        style={{ fontSize: isQuestionContext ? '1.2em' : '1.4em' }}
      >
        {children}
      </h1>
    ),
    h2: ({ children }: any) => (
      <h2 
        className={`${isQuestionContext ? 'font-semibold mb-2 mt-2' : 'font-semibold mb-2 md:mb-3 mt-3 md:mt-5'} first:mt-0 text-right text-primary-700`} 
        dir={effectiveRTL ? "rtl" : "ltr"}
        style={{ fontSize: isQuestionContext ? '1.1em' : '1.2em' }}
      >
        {children}
      </h2>
    ),
    h3: ({ children }: any) => (
      <h3 
        className={`${isQuestionContext ? 'font-semibold mb-1 mt-2' : 'font-semibold mb-2 mt-3 md:mt-4'} first:mt-0 text-right text-primary-600`} 
        dir={effectiveRTL ? "rtl" : "ltr"}
        style={{ fontSize: isQuestionContext ? '1em' : '1.1em' }}
      >
        {children}
      </h3>
    ),
    // Add support for sup tags as a temporary backward compatibility measure
    sup: ({ children }: any) => (
      <sup className="text-xs align-super">{children}</sup>
    ),
    // Add support for sub tags as well
    sub: ({ children }: any) => (
      <sub className="text-xs align-sub">{children}</sub>
    ),
    // Code blocks
    code: ({ inline, children, ...props }: any) => {
      // Check if this is a graph or table placeholder in a code block
      const text = typeof children === 'string' ? children : 
                   Array.isArray(children) ? children.join('') : '';
      
      const graphMatch = graphs.find(graph => text.includes(graph.placeholder));
      
      if (graphMatch && !inline) {
        return (
          <div className={`${isQuestionContext ? 'my-3' : 'my-6'}`}>
            <GraphRenderer 
              config={graphMatch.config}
              onError={(error) => console.error('Graph render error:', error)}
              isRTL={effectiveRTL}
            />
          </div>
        );
      }
      
      const tableMatch = tables.find(table => text.includes(table.placeholder));
      
      if (tableMatch && !inline) {
        return (
          <div className={`${isQuestionContext ? 'my-3' : 'my-6'}`}>
            <TableRenderer 
              config={tableMatch.config}
              onError={(error) => console.error('Table render error:', error)}
              isRTL={effectiveRTL}
            />
          </div>
        );
      }
      
      // Better inline detection - ReactMarkdown's inline prop might not always work
      // Also check if parent is not a pre element and if content looks like inline code
      const isInline = inline || (
        typeof children === 'string' && 
        children.length < 100 && 
        !children.includes('\n') && 
        !text.includes('GRAPH')
      );
      
      if (isInline) {
        return (
          <code 
            className="inline px-1 py-0.5 bg-secondary-100 text-secondary-800 rounded font-mono"
            style={{ display: 'inline' }}
            {...props}
          >
            {children}
          </code>
        );
      }
      
      // Disable old table detection since we're using the new table system
      // The new system uses ```table:data format which is more reliable
      
      return (
        <code className="block p-3 md:p-4 bg-secondary-100 text-secondary-800 rounded-lg font-mono overflow-x-auto mb-3 md:mb-4" {...props}>
          {children}
        </code>
      );
    },
    
    // Pre blocks (often contain code)
    pre: ({ children }: any) => {
      // Check if this pre block contains a graph or table placeholder
      const text = typeof children === 'string' ? children : 
                   Array.isArray(children) ? children.join('') : '';
      
      const graphMatch = graphs.find(graph => text.includes(graph.placeholder));
      
      if (graphMatch) {
        return (
          <div className={`${isQuestionContext ? 'my-3' : 'my-6'}`}>
            <GraphRenderer 
              config={graphMatch.config}
              onError={(error) => console.error('Graph render error:', error)}
              isRTL={effectiveRTL}
            />
          </div>
        );
      }
      
      const tableMatch = tables.find(table => text.includes(table.placeholder));
      
      if (tableMatch) {
        return (
          <div className={`${isQuestionContext ? 'my-3' : 'my-6'}`}>
            <TableRenderer 
              config={tableMatch.config}
              onError={(error) => console.error('Table render error:', error)}
              isRTL={effectiveRTL}
            />
          </div>
        );
      }
      
      // Removed old table detection logic - using new table system
      
      return <pre>{children}</pre>;
    },
    // Blockquotes
    blockquote: ({ children }: any) => (
      <blockquote className="border-r-4 border-primary-400 pr-4 mr-0 mb-4 italic text-secondary-700" dir={effectiveRTL ? "rtl" : "ltr"}>
        {children}
      </blockquote>
    ),
    // Tables
    table: ({ children }: any) => {
      if (tables.length > 0) {
        return null;
      }
      return (
        <div className="overflow-x-auto mb-4 md:mb-6">
          <table className="min-w-full border border-secondary-300 rounded-lg shadow-sm bg-white" dir={effectiveRTL ? 'rtl' : 'ltr'}>
            {children}
          </table>
        </div>
      );
    },
    thead: ({ children }: any) => {
      if (tables.length > 0) {
        return null;
      }
      return (
        <thead className="bg-secondary-50">
          {children}
        </thead>
      );
    },
    tbody: ({ children }: any) => {
      if (tables.length > 0) {
        return null;
      }
      return (
        <tbody className="divide-y divide-secondary-200">
          {children}
        </tbody>
      );
    },
    th: ({ children }: any) => {
      if (tables.length > 0) {
        return null;
      }
      return (
        <th className={`px-3 md:px-4 py-2 md:py-3 ${effectiveRTL ? 'text-right' : 'text-left'} font-semibold text-secondary-800 bg-secondary-100 border-b border-secondary-300 text-sm md:text-base`} dir={effectiveRTL ? 'rtl' : 'ltr'}>
          {children}
        </th>
      );
    },
    td: ({ children }: any) => {
      if (tables.length > 0) {
        return null;
      }
      return (
        <td className={`px-3 md:px-4 py-2 md:py-3 ${effectiveRTL ? 'text-right' : 'text-left'} text-secondary-700 border-b border-secondary-200 text-sm md:text-base align-top`} dir={effectiveRTL ? 'rtl' : 'ltr'}>
          {children}
        </td>
      );
    },
    tr: ({ children }: any) => {
      if (tables.length > 0) {
        return null;
      }
      return (
        <tr className="hover:bg-secondary-25 transition-colors">
          {children}
        </tr>
      );
    },
    // Horizontal rule
    hr: () => (
      <hr className="my-6 border-secondary-300" />
    ),
    // Links
    a: ({ href, children }: any) => (
      <a 
        href={href} 
        className="text-primary-600 hover:text-primary-700 underline"
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
    // Strong/Bold
    strong: ({ children }: any) => {
      // Check if this strong tag contains a graph placeholder
      const text = typeof children === 'string' ? children : 
                   Array.isArray(children) ? children.join('') : '';
      
      const graphMatch = graphs.find(graph => text.includes(graph.placeholder));
      
      if (graphMatch) {
        return (
          <div className={`${isQuestionContext ? 'my-3' : 'my-6'}`}>
            <GraphRenderer 
              config={graphMatch.config}
              onError={(error) => console.error('Graph render error:', error)}
              isRTL={effectiveRTL}
            />
          </div>
        );
      }
      
      const tableMatch = tables.find(table => text.includes(table.placeholder));
      
      if (tableMatch) {
        return (
          <div className={`${isQuestionContext ? 'my-3' : 'my-6'}`}>
            <TableRenderer 
              config={tableMatch.config}
              onError={(error) => console.error('Table render error:', error)}
              isRTL={effectiveRTL}
            />
          </div>
        );
      }
      
      return (
        <strong className="font-bold text-secondary-900">{children}</strong>
      );
    },
    // Emphasis/Italic
    em: ({ children }: any) => (
      <em className="italic">{children}</em>
    ),
    
    // Image rendering
    img: ({ src, alt }: any) => {
      // Convert relative API URLs to absolute backend URLs
      const absoluteSrc = src?.startsWith('/api/') 
        ? `${process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000'}${src}`
        : src;
      
      return (
        <div className={`${isQuestionContext ? 'my-3' : 'my-6'} flex flex-col items-center`}>
          <button
            type="button"
            onClick={() => setImageModal({ src: absoluteSrc, alt })}
            className="cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded-lg"
            aria-label="Open image"
          >
            <img 
              src={absoluteSrc}
              alt={alt || "Generated illustration"}
              className="max-w-full h-auto rounded-lg shadow-lg border border-secondary-200 transition-transform duration-200 hover:scale-[1.01] hover:shadow-xl"
              loading="lazy"
              style={{ maxHeight: '400px', objectFit: 'contain' }}
            />
          </button>
          {alt && (
            <p className="text-sm text-secondary-600 mt-2 text-center italic">
              {alt}
            </p>
          )}
        </div>
      );
    },
    
    // Enhanced Math Rendering
    // KaTeX generates these elements that we can override
    span: ({ className, children, ...props }: any) => {
      // Handle inline math expressions (KaTeX uses span.katex)
      if (className && className.includes('katex')) {
        return (
          <span 
            className={`${className} math-inline`} 
            style={{ 
              direction: 'ltr',
              unicodeBidi: 'isolate',
              display: 'inline-block',
              // Override parent text alignment
              textAlign: 'left',
              // Ensure proper positioning
              verticalAlign: 'baseline',
              // Ensure KaTeX stays within stacking context
              zIndex: 'auto',
              position: 'relative'
            }}
            {...props}
          >
            {children}
          </span>
        );
      }
      return <span className={className} {...props}>{children}</span>;
    },
    
    div: ({ className, children, ...props }: any) => {
      // Handle display math expressions (KaTeX uses div.katex-display)
      if (className && className.includes('katex-display')) {
        return (
          <div 
            className={`${className} math-display group relative my-4`}
            style={{ 
              direction: 'ltr',
              unicodeBidi: 'isolate',
              textAlign: 'center',
              // Ensure proper isolation from RTL context
              isolation: 'isolate',
              // Ensure KaTeX stays within stacking context
              zIndex: 'auto',
              position: 'relative'
            }}
            {...props}
          >
            {children}
            {/* Add copy button for display math */}
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-[2]">
              <button
                onClick={() => {
                  // Try to extract LaTeX source from the content
                  const mathElement = props['data-math-source'] || 'Math expression';
                  navigator.clipboard.writeText(mathElement).catch(console.error);
                }}
                className="bg-white hover:bg-secondary-50 border border-secondary-300 rounded p-1.5 shadow-sm transition-colors"
                title="Copy LaTeX source"
              >
                <svg className="w-3 h-3 text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
          </div>
        );
      }
      return <div className={className} {...props}>{children}</div>;
    },
  }), [tables, graphs, effectiveRTL, isQuestionContext]);

  return (
    <div 
      className={`${isQuestionContext ? 'max-w-none hebrew-text' : 'prose prose-sm md:prose-lg max-w-none hebrew-text'}`}
      style={fontSize ? { fontSize: `${fontSize}px`, lineHeight: '1.6' } : {}}
    >
      {materialType === 'solution' && onFontSizeChange && fontSize && (
        <div className={`flex items-center gap-2 mb-4 ${effectiveRTL ? 'justify-start' : 'justify-end'}`}>
          <button
              onClick={() => handleFontSizeChange('decrease')}
              disabled={fontSize <= 12}
              className="flex items-center justify-center w-6 h-6 rounded-lg bg-surface-subtle hover:bg-surface-hover text-surface-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              title={t('common:actions.decreaseFontSize')}
          >
              <Minus className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium text-surface-secondary w-20 text-center">{t('common:actions.fontSize')}</span>
          <button
              onClick={() => handleFontSizeChange('increase')}
              disabled={fontSize >= 24}
              className="flex items-center justify-center w-6 h-6 rounded-lg bg-surface-subtle hover:bg-surface-hover text-surface-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              title={t('common:actions.increaseFontSize')}
          >
              <Plus className="w-4 h-4" />
          </button>
        </div>
      )}
      {/* Development-only raw markdown toggle */}
      {process.env.NODE_ENV === 'development' && showDevMarkdown && (
        <div className="mb-4 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-yellow-800">Development Tools</span>
            <button
              onClick={() => setShowRawMarkdown(!showRawMarkdown)}
              className="text-yellow-700 hover:text-yellow-900 text-sm underline transition-colors"
            >
              {showRawMarkdown ? 'Show Rendered' : 'Show Raw Markdown'}
            </button>
          </div>
          {showRawMarkdown && (
            <div className="mt-3 text-xs text-secondary-600">
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium">Material Type: {materialType}</span>
                <span>Length: {content.length} chars</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Conditional rendering: raw markdown or processed content */}
      {showRawMarkdown && process.env.NODE_ENV === 'development' && showDevMarkdown ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 font-mono text-sm overflow-x-auto">
          <div className="mb-2 text-xs font-semibold text-gray-600 uppercase tracking-wide">
            Raw Markdown Source:
          </div>
          <pre className="whitespace-pre-wrap text-gray-800 leading-relaxed">
            {content}
          </pre>
        </div>
      ) : (
        <ReactMarkdown 
          remarkPlugins={[remarkGfm, remarkMath]}
          rehypePlugins={[rehypeKatex]}
          components={components}
        >
          {processedContent}
        </ReactMarkdown>
      )}
      {imageModal && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setImageModal(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="relative w-full max-w-5xl"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setImageModal(null)}
              className="absolute -top-10 right-0 text-white/80 hover:text-white text-sm"
              aria-label="Close image"
            >
              Close
            </button>
            <div className="bg-white rounded-lg shadow-xl p-3 md:p-4">
              <img
                src={imageModal.src}
                alt={imageModal.alt || 'Expanded image'}
                className="w-full max-h-[80vh] object-contain rounded-md"
              />
              {imageModal.alt && (
                <p className="text-sm text-secondary-600 mt-3 text-center italic">
                  {imageModal.alt}
                </p>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default StudyMaterialRenderer; 
